import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';

import { PositionLockedPosition, PositionPortfolioResponse, PositionResource } from '../../resources';
import {
  AccountBalance,
  accountBalanceAtom,
  currencyExchangeRateRTAtom,
  CurrencyExchangeRates,
  inventoryDetailsAtom,
} from '../../state';
import {
  Currency,
  CurrencyCross,
  CurrencyCrossObj,
  getInstrumentId,
  getInstrumentIdForce,
  InstrumentCategoryEnum,
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  Inventory,
  ListedInstrument,
  ListedInstrumentLike,
  SelectedAccountOrClient,
} from '../../types';
import {
  groupBy,
  isDefined,
  Loadable,
  LoadableState,
  NbLogger,
  ObjectSet,
  ObjectSetLoadable,
  uniqueArray,
  uniqueArrayBy,
} from '../../utils';

import { IConfig } from '../../config';
import { BaseCurrency } from '../../utils/instrumentUtils';
import { GetListedInstrumentList } from '../instrument';
import { GetResourceHeaderWithToken } from '../useResource';
import { getInventoryId, getTradableInventoryList, updateInventoryMarketPrice } from './inventoryUtils';
import { useAccountBalanceCommon } from './useAccountBalanceCommon';

export type UseInventoryParams = {
  positionResource: PositionResource;
  logger: NbLogger;
  config: IConfig;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  loadInstrument: (
    instruments: Array<ListedInstrument | ListedInstrumentLike>,
    dataScope?: Array<InstrumentSubscriptionDataScope>,
  ) => Promise<ObjectSet<InstrumentStoreItem>>;
  getExchangeRate: (currencyCrosses: Array<CurrencyCrossObj>) => Promise<Map<CurrencyCross, number>>;
  getListedInstrumentList: GetListedInstrumentList;
};

type LockedPositionJoinKeyParams = {
  isin?: string | null;
  accountNumber?: string | null;
  initCcy?: string | null;
  marketMICCode?: string | null;
};

type InventoryLockedPositionJoinKeyParams = Pick<
  Inventory,
  'isin' | 'accountNumber' | 'initCcy' | 'marketMICCode' | 'mainMarketMICCode'
>;

export const normalizeLockedPositionJoinKeyPart = (value: string | undefined | null): string => {
  return value?.trim().toUpperCase() ?? '';
};

export const getLockedPositionJoinKey = ({
  isin,
  accountNumber,
  initCcy,
  marketMICCode,
}: LockedPositionJoinKeyParams): string => {
  return [isin, accountNumber, initCcy, marketMICCode].map(normalizeLockedPositionJoinKeyPart).join('|');
};

export const getInventoryLockedPositionJoinKey = (inventory: InventoryLockedPositionJoinKeyParams): string => {
  return getLockedPositionJoinKey({
    isin: inventory.isin,
    accountNumber: inventory.accountNumber,
    initCcy: inventory.initCcy,
    marketMICCode: inventory.marketMICCode ?? inventory.mainMarketMICCode,
  });
};

export const getLockedPositionMap = (lockedPositionList: Array<PositionLockedPosition>): Map<string, number> => {
  return lockedPositionList.reduce((acc, lockedPosition) => {
    const key = getLockedPositionJoinKey({
      isin: lockedPosition.isin,
      accountNumber: lockedPosition.accountNumber,
      initCcy: lockedPosition.initCcy,
      marketMICCode: lockedPosition.marketMICCode,
    });
    const currentLockedQuantity = acc.get(key) ?? 0;
    acc.set(key, currentLockedQuantity + lockedPosition.lockedPosition);
    return acc;
  }, new Map<string, number>());
};

export const resolveLockedPositionMap = (
  lockedPositionList: Array<PositionLockedPosition> | undefined,
  previousLockedPositionMap: Map<string, number>,
): Map<string, number> => {
  if (lockedPositionList) {
    return getLockedPositionMap(lockedPositionList);
  }
  return previousLockedPositionMap;
};

export const mapInventoryListWithLockedQuantity = <T extends InventoryLockedPositionJoinKeyParams>(
  inventoryList: Array<T>,
  lockedPositionMap: Map<string, number>,
): Array<T & { lockedQuantity: number }> => {
  const assignedLockedQuantityKeys = new Set<string>();
  return inventoryList.map(inventory => {
    const key = getInventoryLockedPositionJoinKey(inventory);
    const lockedQuantity = lockedPositionMap.get(key) ?? 0;

    if (lockedQuantity <= 0 || assignedLockedQuantityKeys.has(key)) {
      return {
        ...inventory,
        lockedQuantity: 0,
      };
    }

    assignedLockedQuantityKeys.add(key);
    return {
      ...inventory,
      lockedQuantity,
    };
  });
};

export const useInventoryCommon = ({
  positionResource,
  getResourceHeaderWithToken,
  logger,
  config,
  loadInstrument,
  getListedInstrumentList,
  getExchangeRate,
}: UseInventoryParams) => {
  const [inventoryDetails, setInventoryDetails] = useAtom(inventoryDetailsAtom);
  const setCurrencyExchangeRate = useSetAtom(currencyExchangeRateRTAtom);
  const setAccountBalance = useSetAtom(accountBalanceAtom);
  const lastLockedPositionMapRef = useRef<Map<string, number>>(new Map());

  const { updateAccountBalance } = useAccountBalanceCommon();

  /**
   * Merges the freshly fetched exchange rates into the currency exchange rate atom.
   * @private
   */
  const mergeExchangeRates = useCallback(
    (exchangeRates: Map<CurrencyCross, number>) => {
      if (exchangeRates.size === 0) {
        return;
      }
      setCurrencyExchangeRate((curr: CurrencyExchangeRates) => {
        const updatedExchangeRates = { ...curr };
        exchangeRates.forEach((rate, currencyCross) => {
          updatedExchangeRates[currencyCross] = Loadable.loaded({
            currencyCross,
            rateInHuf: rate,
            lastUpdated: new Date().toISOString(),
          });
        });
        return updatedExchangeRates;
      });
      logger.debug(`Exchange rates updated (${Array.from(exchangeRates.entries()).map(([cc, r]) => `${cc}=${r}`)})`);
    },
    [setCurrencyExchangeRate, logger],
  );

  const updateAccountBalances = useCallback(
    (portfolio: PositionPortfolioResponse) => {
      const accountBalanceList = new ObjectSetLoadable<AccountBalance, string>(
        item => item.accountNumber,
        portfolio.accountBalanceList.map(account => ({ ...account, isLoading: false })),
      );
      updateAccountBalance(accountBalanceList);
    },
    [updateAccountBalance],
  );

  const getListedInstrumentLikesForEStreamRequest = useCallback(
    (inventoryList: Array<Inventory>, currencyList: Array<Currency>) => {
      const currencyCrosses = currencyList
        .filter(_ => _ !== config.NetBroker.BaseCurrency)
        .map(currency => `${currency}${config.NetBroker.BaseCurrency}=`);

      const listedInstrumentLikes = inventoryList.reduce((acc, next) => {
        if (next.ric && (next.mainMarketMICCode || next.marketMICCode) && next.instrumentCategory) {
          acc.add({
            ric: next.ric,
            mic: next.mainMarketMICCode || next.marketMICCode || '',
            instrumentCategory: next.instrumentCategory as InstrumentCategoryEnum,
            lastClosePrice: next.marketPrice / next.position,
            currency: next.marketPriceCcy,
          });
        }
        return acc;
      }, new ObjectSet<ListedInstrumentLike>(getInstrumentIdForce));
      currencyCrosses.forEach(currencyCross => {
        listedInstrumentLikes.add({
          ric: currencyCross,
          mic: '',
          instrumentCategory: InstrumentCategoryEnum.Currency,
        });
      });

      return listedInstrumentLikes;
    },
    [],
  );

  /**
   * Maps an InstrumentStoreItem to an Inventory item
   *
   * This is needed because we need to update the inventory's marketPrice with the latest price from EStream
   * and sometimes the inventory market price currency is different from the initial currency, so we need to convert it
   * using an exchange rate (coming from estream).
   *
   * This usually happens when the instrument is bought in a different market, not the instrument's main market.
   * For example: WIZZ's main market is the London Stock Exchange, but it can be bought in Budapest Stock Exchange as well.
   * In this case the market price coming from FRONT is in HUF, but the initial currency is GBP.
   *
   * @private
   */
  const mapInventoryWithMarketPrice = useCallback(
    (instrumentStoreItems: ObjectSet<InstrumentStoreItem>, exchangeRates: Map<CurrencyCross, number>) => {
      return (inventory: Inventory) => {
        // set market price for the inventory item if it's available for the given inventory item
        const instrumentId = inventory.ric ? getInstrumentId(inventory) : undefined;
        if (instrumentId) {
          const instrumentStoreItem = instrumentStoreItems.get(instrumentId);
          if (instrumentStoreItem) {
            const hasEStreamData = isDefined(instrumentStoreItem.price?.value) && isDefined(instrumentStoreItem.ccy);
            if (!hasEStreamData) {
              logger.warn(`No EStream data for ${instrumentId}`);
            }

            if (!hasEStreamData && !inventory.marketPrice) {
              inventory.isIncorrect = true;
            }
            // latest price of the instrument can be calculated from estream data, but we are preparing for fallback, when
            // price is not available in estream
            const latestPrice = hasEStreamData
              ? instrumentStoreItem.price?.value
              : inventory.marketPrice / inventory.position;
            const latestPriceCcy = hasEStreamData ? instrumentStoreItem.ccy : inventory.marketPriceCcy;
            // THESE errors should not happen, but for type safety we are checking them
            // unrecoverable error if we don't have a price for the instrument
            if (!isDefined(latestPrice)) {
              logger.error(`Failed to get latest price for ${instrumentId}`);
              return undefined;
            }

            // unrecoverable error if we don't have a currency for the instrument, so can't decide the exchange rate
            if (!isDefined(latestPriceCcy)) {
              logger.error(`Failed to get currency for ${instrumentId} however we got a price for it`);
              return undefined;
            }

            updateInventoryMarketPrice({
              inventory,
              latestPriceCcy,
              latestPrice,
              config,
              exchangeRates,
              logger,
            });
          }
        }
        // In therory this can handle cases when there is no ric code for OTC instruments, by updating grossProfitLoss
        //
        // else if (inventory.ric === undefined && isDefined(inventory.grossProfitLossHuf) && inventory.marketPriceCcy !== config.NetBroker.BaseCurrency) {
        //   const initCcyBaseCurrencyExchangeRate = 1 / (exchangeRates.get(`${inventory.marketPriceCcy}${config.NetBroker.BaseCurrency}=` as CurrencyCross) || 365);
        //   if (!initCcyBaseCurrencyExchangeRate) {
        //     throw new Error(`Failed to get exchange rate for ${inventory.marketPriceCcy}=`);
        //   }
        //
        //   inventory.grossProfitLoss = inventory.grossProfitLossHuf * initCcyBaseCurrencyExchangeRate;
        // }
        return inventory;
      };
    },
    [logger],
  );

  /**
   * Collects currency crosses from the inventory and cash accounts
   * @private
   */
  const collectCurrenciesFromInventory = useCallback(
    (inventoryList: Array<Inventory>, currencyList: Array<Currency>): Array<CurrencyCrossObj> => {
      const currencies = inventoryList.reduce((acc, next) => {
        if (next.marketPriceCcy !== next.initCcy) {
          acc.push({ base: next.marketPriceCcy as Currency, quote: next.initCcy as Currency });
        }
        if (next.initCcy !== config.NetBroker.BaseCurrency) {
          acc.push({ base: next.initCcy as Currency, quote: BaseCurrency });
        }
        return acc;
      }, [] as Array<CurrencyCrossObj>);
      currencyList.forEach(currency => {
        currencies.push({ base: currency, quote: BaseCurrency });
      });
      return currencies;
    },
    [],
  );

  /**
   * Collects currency crosses that are only known after loading instrument data from EStream.
   * This covers the case where the EStream instrument currency (instrumentStoreItem.ccy) differs
   * from the backend marketPriceCcy, so the required cross wouldn't be in collectCurrenciesFromInventory.
   * @private
   */
  const collectCurrencyCrossesFromInstrumentStore = useCallback(
    (
      inventoryList: Array<Inventory>,
      instrumentStoreItems: ObjectSet<InstrumentStoreItem>,
    ): Array<CurrencyCrossObj> => {
      return inventoryList.reduce((acc, inventory) => {
        if (!inventory.ric) {
          return acc;
        }
        const instrumentId = getInstrumentId(inventory);
        if (!instrumentId) {
          return acc;
        }
        const instrumentStoreItem = instrumentStoreItems.get(instrumentId);
        if (!instrumentStoreItem?.ccy) {
          return acc;
        }
        const estreamCcy = instrumentStoreItem.ccy as Currency;
        if (estreamCcy !== inventory.initCcy) {
          acc.push({ base: estreamCcy, quote: inventory.initCcy as Currency });
        }
        if (estreamCcy !== (BaseCurrency as Currency)) {
          acc.push({ base: estreamCcy, quote: BaseCurrency });
        }
        return acc;
      }, [] as Array<CurrencyCrossObj>);
    },
    [],
  );

  /**
   * Collects currency crosses for the case where the listed instrument data (REST) reports a
   * different trading currency for a RIC than the one carried by the inventory item (initCcy).
   * This currency is only known after getListedInstrumentList resolves, so the cross would not be
   * covered by collectCurrenciesFromInventory / collectCurrencyCrossesFromInstrumentStore.
   * @private
   */
  const collectCurrencyCrossesFromInstrumentList = useCallback(
    (inventoryList: Array<Inventory>, instrumentList: Array<ListedInstrument>): Array<CurrencyCrossObj> => {
      return inventoryList.reduce((acc, inventory) => {
        if (!inventory.ric) {
          return acc;
        }
        const instrument = instrumentList.find(i => i.ric === inventory.ric);
        if (!instrument?.currency) {
          return acc;
        }
        const instrumentCcy = instrument.currency as Currency;
        if (instrumentCcy !== inventory.initCcy) {
          acc.push({ base: instrumentCcy, quote: inventory.initCcy as Currency });
        }
        if (instrumentCcy !== (BaseCurrency as Currency)) {
          acc.push({ base: instrumentCcy, quote: BaseCurrency });
        }
        return acc;
      }, [] as Array<CurrencyCrossObj>);
    },
    [],
  );

  /**
   * Sets up the raw inventory data for the UI
   *
   *  - creates unique ID for inventory item
   *  - calculates market price for a single inventory item (marketPriceSingle)
   *  - loads the latest instrument price data from EStream
   *    -> updates market price for the inventory item
   *    -> updates exchange rate
   */
  const updatePriceDataFromEStream = useCallback(
    async (
      inventoryList: Array<Inventory>,
      currencyList: Array<Currency>,
    ): Promise<{ inventory: Array<Inventory>; currencyCrosses: Array<CurrencyCrossObj> }> => {
      const listedInstrumentLikes = getListedInstrumentLikesForEStreamRequest(inventoryList, currencyList);
      const instrumentStoreItems = await loadInstrument(Array.from(listedInstrumentLikes.values()));

      const currencyCrosses = collectCurrenciesFromInventory(inventoryList, currencyList);
      const instrumentStoreCrosses = collectCurrencyCrossesFromInstrumentStore(inventoryList, instrumentStoreItems);
      const allCurrencyCrosses = [...currencyCrosses, ...instrumentStoreCrosses];
      const exchangeRates = await getExchangeRate(allCurrencyCrosses);

      mergeExchangeRates(exchangeRates);

      return {
        inventory: inventoryList
          .map(mapInventoryWithMarketPrice(instrumentStoreItems, exchangeRates))
          .filter(isDefined),
        currencyCrosses: allCurrencyCrosses,
      };
    },
    [
      loadInstrument,
      getListedInstrumentLikesForEStreamRequest,
      collectCurrenciesFromInventory,
      collectCurrencyCrossesFromInstrumentStore,
      mapInventoryWithMarketPrice,
      getExchangeRate,
      mergeExchangeRates,
    ],
  );

  /**
   * Returns a list of unique currencies from the portfolio to subscribe to
   */
  const getCurrencyList = useCallback((portfolio: PositionPortfolioResponse): Array<Currency> => {
    // Get all currencies from cash accounts
    //  This will be used to subscribe to currency changes (estream)
    const cashCurrencies = uniqueArray(
      portfolio.accountBalanceList.reduce((acc, next) => {
        if (next.cashInfo) {
          const currencies = Object.keys(next.cashInfo);
          acc.push(...currencies);
        }

        return acc;
      }, [] as string[]),
    );

    const inventoryInitCurrencies = uniqueArrayBy(portfolio.inventoryList, 'initCcy');
    const inventoryMarketCurrencies = uniqueArrayBy(portfolio.inventoryList, 'marketPriceCcy');

    // merge cash and inventory currencies and store it in the inventory
    return uniqueArray([
      ...cashCurrencies,
      ...inventoryInitCurrencies,
      ...inventoryMarketCurrencies,
    ]) as Array<Currency>;
  }, []);

  const getInventoryInstrumentRics = useCallback(
    (
      inventoryList: Array<Inventory>,
    ): {
      inventoryInstrumentRics: Array<string>;
      inventoryItemsGroupedByInstrument: Record<string, Array<Inventory>>;
      nonTradableInventories: Array<Inventory>;
      tradableInventoriesWithoutInstrument: Array<Inventory>;
    } => {
      const tradableInventoriesWithoutInstrument = inventoryList.filter(inventory => isDefined(inventory.ric));
      const nonTradableInventories = inventoryList.filter(inventory => !isDefined(inventory.ric));

      const inventoryItemsGroupedByInstrument: Record<string, Array<Inventory>> = groupBy(
        tradableInventoriesWithoutInstrument,
        'ric',
      );
      const inventoryInstrumentRics = Object.keys(inventoryItemsGroupedByInstrument);

      return {
        inventoryInstrumentRics,
        inventoryItemsGroupedByInstrument,
        nonTradableInventories,
        tradableInventoriesWithoutInstrument,
      };
    },
    [],
  );

  const inventoryNamePostFixMapper = useCallback((inventoryList: Inventory[]) => {
    const ricToCcySet = new Map<string, Set<string>>();
    for (const item of inventoryList) {
      if (item.ric) {
        if (!ricToCcySet.has(item.ric)) {
          ricToCcySet.set(item.ric, new Set());
        }
        ricToCcySet.get(item.ric)!.add(item.initCcy);
      }
    }

    return inventoryList.map(item => ({
      ...item,
      namePostfix: item.ric ? (ricToCcySet.get(item.ric)!.size > 1 ? ` - ${item.initCcy}` : undefined) : undefined,
    }));
  }, []);

  /**
   * Parses and saves the portfolio
   */
  const parseAndSavePortfolio = useCallback(
    async (portfolio: PositionPortfolioResponse) => {
      logger.info('Parsing and saving portfolio', {
        accountBalanceList: portfolio.accountBalanceList.map(_ => _.accountNumber),
        inventoryList: portfolio.inventoryList.map(_ => `${_.instrumentName}(${_.position})`),
      });

      lastLockedPositionMapRef.current = resolveLockedPositionMap(
        portfolio.lockedPositionList,
        lastLockedPositionMapRef.current,
      );
      const inventoryListWithLockedQuantity = mapInventoryListWithLockedQuantity(
        portfolio.inventoryList,
        lastLockedPositionMapRef.current,
      );

      const inventoryListWithOutDatedPrices = inventoryListWithLockedQuantity.map(beInventory => ({
        ...beInventory,
        id: getInventoryId(beInventory),
        marketPriceSingle: beInventory.position ? beInventory.marketPrice / beInventory.position : 0,
      }));

      const inventoryListWithNamePostfix = inventoryNamePostFixMapper(inventoryListWithOutDatedPrices);

      const currencyList = getCurrencyList(portfolio);

      // map the backend inventory to fill the missing info
      const inventoryListPromise = updatePriceDataFromEStream(inventoryListWithNamePostfix, currencyList);

      const {
        inventoryInstrumentRics,
        inventoryItemsGroupedByInstrument,
        nonTradableInventories,
        tradableInventoriesWithoutInstrument,
      } = getInventoryInstrumentRics(inventoryListWithNamePostfix);

      // get the listed instruments for the tradable inventories
      const instrumentListPromise: Promise<Array<ListedInstrument>> = getListedInstrumentList(inventoryInstrumentRics);

      // wait for the inventory and instrument list to be resolved
      const [inventoryListWrapper, instrumentList] = await Promise.all([inventoryListPromise, instrumentListPromise]);

      // the listed instrument data (REST) may report a different trading currency for a RIC than the one
      // carried by the inventory item, so fetch and store the rates for those crosses too
      const instrumentListCrosses = collectCurrencyCrossesFromInstrumentList(
        inventoryListWithNamePostfix,
        instrumentList,
      );
      const missingCurrencyCrosses = instrumentListCrosses.filter(
        cross => !inventoryListWrapper.currencyCrosses.some(c => c.base === cross.base && c.quote === cross.quote),
      );
      if (missingCurrencyCrosses.length > 0) {
        await getExchangeRate(missingCurrencyCrosses).then(mergeExchangeRates);
      }

      updateAccountBalances({
        accountBalanceList: portfolio.accountBalanceList,
        inventoryList: [...inventoryListWrapper.inventory],
        lockedPositionList: portfolio.lockedPositionList,
      });

      const { tradableInventories, incorrectInventories } = getTradableInventoryList(
        inventoryInstrumentRics,
        inventoryItemsGroupedByInstrument,
        instrumentList,
        tradableInventoriesWithoutInstrument,
      );

      logger.debug(`Parsed and saved portfolio`, { tradableInventories, nonTradableInventories });
      setInventoryDetails(
        Loadable.loaded({
          tradableInventories,
          inventories: [tradableInventories, incorrectInventories, nonTradableInventories].flat(),
          nonTradableInventories: [nonTradableInventories, incorrectInventories].flat(),
          currencyList,
          currencyCrosses: [...inventoryListWrapper.currencyCrosses, ...missingCurrencyCrosses],
        }),
      );
    },
    [
      getListedInstrumentList,
      setInventoryDetails,
      updateAccountBalances,
      logger,
      getCurrencyList,
      updatePriceDataFromEStream,
      getInventoryInstrumentRics,
      inventoryNamePostFixMapper,
      collectCurrencyCrossesFromInstrumentList,
      getExchangeRate,
      mergeExchangeRates,
    ],
  );

  /**
   * Loads portfolio composition from the backend (REST)
   */
  const loadInventoryDetails = useCallback(async (): Promise<void> => {
    setInventoryDetails(Loadable.loading);
    setAccountBalance(new ObjectSetLoadable<AccountBalance>(accountBalance => accountBalance.accountNumber));
    const response = await positionResource.getPortfolio(getResourceHeaderWithToken());
    const positionResponse: PositionPortfolioResponse = response.data;

    await parseAndSavePortfolio(positionResponse);
  }, [setInventoryDetails, positionResource, getResourceHeaderWithToken, parseAndSavePortfolio, setAccountBalance]);

  /**
   * Returns the inventory count for the given RIC and account number
   */
  const getInventoryCount = useCallback(
    (ric: string, accountNumber: string): LoadableState<number> => {
      if (!inventoryDetails.isLoaded) {
        return Loadable.loading;
      }
      return Loadable.loaded(
        inventoryDetails.inventories
          ?.filter(inventory => inventory.ric === ric && inventory.accountNumber === accountNumber)
          .reduce((prev, curr) => prev + curr.position, 0) || 0,
      );
    },
    [inventoryDetails],
  );

  /**
   /* Returns the inventory average price for the given RIC and account number
   /*/
  const getInventoryAveragePrice = useCallback(
    (ric: string, accountNumber: string): LoadableState<number> => {
      const inventoryCount = getInventoryCount(ric, accountNumber);
      if (!inventoryDetails.isLoaded || !inventoryCount.isLoaded) {
        return Loadable.loading;
      }

      const inventoryInitPriceSum = inventoryDetails.inventories
        .filter(inventory => inventory.ric === ric && inventory.accountNumber === accountNumber)
        .reduce((prev, curr) => prev + curr.initPriceValue, 0);

      if (inventoryCount.value === 0) {
        return Loadable.loaded(0);
      }

      return Loadable.loaded(inventoryInitPriceSum / inventoryCount.value);
    },
    [inventoryDetails, getInventoryCount],
  );

  /**
   * Returns true if the selected account include the iventory
   */
  const getHasInventory = useCallback(
    (ric: string, selectedAccountOrClient: SelectedAccountOrClient): boolean => {
      if (!inventoryDetails.isLoaded) {
        return false;
      }

      if (selectedAccountOrClient.client) {
        return inventoryDetails.inventories.some(i => i.ric === ric && i.clientId === selectedAccountOrClient.client);
      } else if (selectedAccountOrClient.account) {
        return inventoryDetails.inventories.some(
          i => i.ric === ric && i.accountNumber === selectedAccountOrClient.account.accountNumber,
        );
      }

      return false;
    },
    [inventoryDetails],
  );

  return useMemo(
    () => ({
      inventoryDetails: inventoryDetails || { isLoading: true, inventories: [] },
      loadInventoryDetails,
      getInventoryCount,
      parseAndSavePortfolio,
      getInventoryAveragePrice,
      getHasInventory,
    }),
    [inventoryDetails, loadInventoryDetails, parseAndSavePortfolio, getInventoryCount],
  );
};
